import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ShieldAlert } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

export default function OverrideApproveDialog({ open, entryIds, label, token, onClose, onDone }) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const count = entryIds?.length || 0;

  const submit = async () => {
    if (reason.trim().length < 10) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/time/admin-approve-override-bulk`,
        { entry_ids: entryIds, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast({
        title: `${data.approved} shift${data.approved === 1 ? '' : 's'} approved with override`,
        description: data.skipped ? `${data.skipped} skipped: ${data.skipped_reasons.map((s) => s.reason).join('; ')}` : 'Workers were notified in-app.',
      });
      setReason('');
      onDone?.();
      onClose();
    } catch (e) {
      toast({ title: 'Override failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="override-approve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-500" /> Approve with admin override</DialogTitle>
          <DialogDescription>
            {count} shift{count === 1 ? '' : 's'} ({label}) will be marked <strong>payroll approved</strong> without the worker&apos;s
            certification or supervisor review. This is logged in the audit trail and the worker is notified.
          </DialogDescription>
        </DialogHeader>
        <label className="text-xs font-medium text-slate-300">Reason (required, min 10 characters)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Worker did not certify before the Monday cut-off; hours confirmed with supervisor by phone."
          className="w-full rounded-lg px-3 py-2 text-sm"
          style={{ background: '#0B1321', color: '#fff', border: '1px solid #2D3748' }}
          data-testid="override-reason-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="override-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 10}
            className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="override-confirm-btn">
            {busy ? 'Approving…' : `Approve ${count} with override`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
